import React, { useEffect, useRef } from 'react';

// Animated hexagonal graphene lattice canvas background.
// Slow drift + subtle pulsing nodes. Uses requestAnimationFrame
// with a ref-based frame loop — no React state updates.
const GrapheneLattice = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w = 0, h = 0, dpr = 1;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.width = window.innerWidth * dpr;
      h = canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    const HEX_SIZE = 26 * dpr;
    const HORIZ = HEX_SIZE * Math.sqrt(3);
    const VERT = HEX_SIZE * 1.5;
    let t = 0;

    const drawHex = (cx, cy, size, alpha) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        const x = cx + size * Math.cos(a);
        const y = cy + size * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(0, 255, 157, ${alpha})`;
      ctx.lineWidth = 1 * dpr;
      ctx.stroke();
    };

    const drawNode = (cx, cy, alpha) => {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 3 * dpr);
      g.addColorStop(0, `rgba(0, 255, 157, ${alpha})`);
      g.addColorStop(1, 'rgba(0, 255, 157, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, 3 * dpr, 0, Math.PI * 2);
      ctx.fill();
    };

    const render = () => {
      t += 0.005;
      ctx.clearRect(0, 0, w, h);

      const offsetX = (t * 8) % HORIZ;
      const offsetY = (t * 4) % VERT;

      const rows = Math.ceil(h / VERT) + 3;
      const cols = Math.ceil(w / HORIZ) + 3;

      for (let r = -1; r < rows; r++) {
        for (let c = -1; c < cols; c++) {
          const cx = c * HORIZ + ((r % 2) * HORIZ / 2) - offsetX;
          const cy = r * VERT - offsetY;
          const distX = cx - w / 2;
          const distY = cy - h / 2;
          const dist = Math.sqrt(distX * distX + distY * distY);
          const fade = Math.max(0.03, 0.12 - dist / (Math.max(w, h) * 3));
          const pulse = 0.5 + 0.5 * Math.sin(t * 2 + (r + c) * 0.4);
          drawHex(cx, cy, HEX_SIZE * 0.9, fade * (0.5 + pulse * 0.5));
          if ((r + c) % 5 === 0) {
            drawNode(cx, cy, fade * pulse * 2);
          }
        }
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="bg-layer">
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default GrapheneLattice;
